import { SingSetup, SongPreview } from '~/interfaces';
import { NoteStats } from '~/modules/game-engine/game-state/helpers/calculate-note-stats';
import SongsService from '~/modules/songs/songs-service';
import storage from '~/modules/utils/storage';

let store: Promise<LocalForage | typeof storage.memory> | null = null;

async function getStorage() {
  if (!store) {
    if ('localStorage' in globalThis) {
      try {
        store = import('localforage').then(({ default: localForage }) =>
          localForage.createInstance({ name: 'localforage' }),
        );
      } catch (e) {
        console.error(e);
        store = Promise.resolve(storage.memory);
      }
    } else {
      store = Promise.resolve(storage.memory);
    }
  }
  return store;
}

export interface SongStats {
  plays: number;
  scores: Array<{
    setup: SingSetup;
    /** Exactly what `songEnded` reported - `notes` is absent on plays recorded before it was
     * measured, and on co-op, whose single entry is the whole team's. */
    scores: Array<{ name: string; score: number; notes?: NoteStats }>;
    date: string;
    progress?: number;
  }>;
}

export const getSongKeyOld = (song: Pick<SongPreview, 'artist' | 'title'>) =>
  `${song.artist}-${song.title}`.toLowerCase();
/**
 * The key a play is filed under. Asks for the `id` because that is what it actually uses: this
 * forwards to `getSongId`, which prefers the id and only slugs the artist and title when there is
 * none. Asking only for those two while depending on a third meant a caller could hand over
 * exactly what the type demanded and have its plays filed somewhere else entirely - so the type
 * now says so, and anything that cannot supply an id is a compile error rather than a silent
 * mismatch. Nothing stored is re-keyed by this.
 */
export const getSongKey = (song: Pick<SongPreview, 'artist' | 'title' | 'id'>) => SongsService.generateSongFile(song);

export const fetchSongStats = async (song: Pick<SongPreview, 'artist' | 'title' | 'id'>) => {
  const storageKey = getSongKey(song);
  return (await (await getStorage())?.getItem<SongStats>(storageKey)) || { plays: 0, scores: [] };
};

export const storeSongStats = async (song: Pick<SongPreview, 'artist' | 'title' | 'id'>, stats: SongStats) => {
  await (await getStorage())?.setItem<SongStats>(getSongKey(song), stats);
};

export const getAllStats = async () => {
  const keys = (await (await getStorage())?.keys()) ?? [];
  const stats: Record<string, SongStats> = {};
  await Promise.all(
    keys.map(async (key) => {
      stats[key] = (await (await getStorage())?.getItem<SongStats>(key))!;
    }),
  );

  return stats;
};
